import React, { useState } from "react";
import {
  Dialog,
  Portal,
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { FiX, FiDownload, FiExternalLink, FiFile } from "react-icons/fi";

const CorporateDocumentViewer = ({ isOpen, onClose, documents }) => {
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  
  if (!documents || documents.length === 0) {
    return null;
  }

  const currentDocument = documents[currentDocumentIndex];
  const isImage = currentDocument?.fileUrl && 
    (currentDocument.fileUrl.toLowerCase().endsWith('.jpg') ||
     currentDocument.fileUrl.toLowerCase().endsWith('.jpeg') ||
     currentDocument.fileUrl.toLowerCase().endsWith('.png') ||
     currentDocument.fileUrl.toLowerCase().endsWith('.gif') ||
     currentDocument.fileUrl.toLowerCase().endsWith('.webp'));

  const isPDF = currentDocument?.fileUrl && 
    currentDocument.fileUrl.toLowerCase().endsWith('.pdf');

  const handleDownload = () => {
    if (currentDocument?.fileUrl) {
      window.open(currentDocument.fileUrl, '_blank');
    }
  };

  const handleNext = () => {
    setCurrentDocumentIndex((prev) => (prev + 1) % documents.length);
  };

  const handlePrevious = () => {
    setCurrentDocumentIndex((prev) => (prev - 1 + documents.length) % documents.length);
  };

  return (
    <Dialog.Root open={isOpen} size="xl" placement="center" motionPreset="scale">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="90vw"
            maxH="90vh"
            p={4}
            borderRadius="md"
            bg="white"
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header display="flex" justifyContent="space-between" alignItems="center">
              <Dialog.Title>
                Document Viewer
              </Dialog.Title>
              <Button size="sm" variant="ghost" onClick={onClose}>
                <FiX />
              </Button>
            </Dialog.Header>

            <Dialog.Body
              pt={4}
              flexGrow={1}
              display="flex"
              flexDirection="column"
              gap={4}
              alignItems="center"
              justifyContent="center"
            >
              {!currentDocument?.fileUrl ? (
                <VStack spacing={4}>
                  <Text color="gray.500" fontSize="lg">No document available</Text>
                  <Text fontSize="sm" color="gray.400">
                    {currentDocument?.type || "Document"}
                  </Text>
                </VStack>
              ) : isImage ? (
                <Box
                  maxW="100%"
                  maxH="60vh"
                  overflow="hidden"
                  borderRadius="md"
                  boxShadow="md"
                >
                  <Image
                    src={currentDocument.fileUrl}
                    alt={currentDocument.type}
                    objectFit="contain"
                    maxW="100%"
                    maxH="60vh"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              ) : isPDF ? (
                <Box
                  w="100%"
                  h="60vh"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={4}
                >
                  <FiFile size={48} color="#718096" />
                  <Text fontWeight="medium">PDF Document</Text>
                  <Text fontSize="sm" color="gray.500">
                    {currentDocument.type}
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={handleDownload}
                    leftIcon={<FiExternalLink />}
                  >
                    Open PDF
                  </Button>
                </Box>
              ) : (
                <VStack spacing={4}>
                  <FiFile size={48} color="#718096" />
                  <Text fontWeight="medium">Document</Text>
                  <Text fontSize="sm" color="gray.500">
                    {currentDocument.type}
                  </Text>
                  <Button
                    colorScheme="blue"
                    onClick={handleDownload}
                    leftIcon={<FiExternalLink />}
                  >
                    View Document
                  </Button>
                </VStack>
              )}

              {currentDocument?.fileUrl && (
                <VStack spacing={2} mt={4}>
                  <Text fontSize="sm" fontWeight="medium">
                    {currentDocument.type}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Uploaded: {currentDocument.uploadedAt 
                      ? new Date(currentDocument.uploadedAt).toLocaleDateString()
                      : 'N/A'
                    }
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Status: {currentDocument.status || 'pending'}
                  </Text>
                </VStack>
              )}
            </Dialog.Body>

            {documents.length > 1 && (
              <Dialog.Footer>
                <HStack spacing={4} justifyContent="center" w="100%">
                  <Button
                    size="sm"
                    onClick={handlePrevious}
                    disabled={currentDocumentIndex === 0}
                  >
                    Previous
                  </Button>
                  <Text fontSize="sm" color="gray.500">
                    {currentDocumentIndex + 1} of {documents.length}
                  </Text>
                  <Button
                    size="sm"
                    onClick={handleNext}
                    disabled={currentDocumentIndex === documents.length - 1}
                  >
                    Next
                  </Button>
                </HStack>
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CorporateDocumentViewer;